import { v4 as uuidv4 } from "uuid";
import {
    AccessRule,
    AccessAction,
    AccessRuleMode,
    AccessRuleStatus,
    TableAccessConfig
} from "@/types/table-access";

export class TableAccessManager {
    /**
     * Serializes access configuration to JSON string for storage in data attribute
     */
    static serialize(config: TableAccessConfig): string {
        const compact = {
            v: 1,
            r: config.rules.map((rule) => {
                const compactRule: Record<string, unknown> = {
                    n: rule.name,
                    m: rule.mode === AccessRuleMode.ROLE ? 0 : rule.mode === AccessRuleMode.USER ? 2 : 1,
                    a: rule.action === AccessAction.SHOW ? 1 : 0,
                    c: rule.columns
                };

                const compactStatus =
                    rule.status === AccessRuleStatus.INACTIVE
                        ? 0
                        : rule.status === AccessRuleStatus.EXPORT_SHARE
                            ? 2
                            : 1;
                compactRule.s = compactStatus;

                if (rule.mode === AccessRuleMode.ROLE) {
                    if (rule.allRoles) {
                        compactRule.al = 1;
                    } else if (rule.roleIds.length > 0) {
                        compactRule.r = rule.roleIds;
                    }
                } else if (rule.mode === AccessRuleMode.USER) {
                    if (rule.userIds.length > 0) {
                        compactRule.u = rule.userIds;
                    }
                } else if (rule.query.trim()) {
                    compactRule.q = rule.query;
                }

                return compactRule;
            })
        };

        return JSON.stringify(compact);
    }

    /**
     * Deserializes JSON string from data attribute back to object
     */
    static deserialize(jsonString: string | null): TableAccessConfig | null {
        if (!jsonString) return null;
        try {
            const parsed: unknown = JSON.parse(jsonString);

            const asAny = parsed as any;
            if (asAny && typeof asAny === "object" && "v" in asAny && "r" in asAny) {
                const rules: AccessRule[] = (Array.isArray(asAny.r) ? asAny.r : []).map(
                    (r: any, index: number) => {
                        const mode: AccessRuleMode =
                            r?.m === 2
                                ? AccessRuleMode.USER
                                : r?.m === 1
                                    ? AccessRuleMode.QUERY
                                    : AccessRuleMode.ROLE;
                        const status: AccessRuleStatus =
                            r?.s === 2
                                ? AccessRuleStatus.EXPORT_SHARE
                                : r?.s === 0
                                    ? AccessRuleStatus.INACTIVE
                                    : r?.e === 0
                                        ? AccessRuleStatus.INACTIVE
                                        : AccessRuleStatus.ACTIVE;
                        return {
                            id: uuidv4(),
                            name: typeof r?.n === "string" && r.n.trim() ? r.n : `قانون ${index + 1}`,
                            mode,
                            allRoles: r?.al === 1,
                            roleIds: Array.isArray(r?.r) ? r.r.filter((x: unknown) => typeof x === "number") : [],
                            userIds: Array.isArray(r?.u) ? r.u.filter((x: unknown) => typeof x === "number") : [],
                            query: typeof r?.q === "string" ? r.q : "",
                            columns: Array.isArray(r?.c) ? r.c.filter((x: unknown) => typeof x === "string") : [],
                            action: r?.a === 1 ? AccessAction.SHOW : AccessAction.HIDE,
                            status
                        };
                    }
                );

                return {
                    version: 2,
                    tableId: "",
                    rules
                };
            }

            // Legacy fallback (v1) - keeps columns/query/name but cannot reliably map string roles to roleIds
            if (asAny && typeof asAny === "object" && Array.isArray(asAny.rules)) {
                const legacyRules: AccessRule[] = asAny.rules.map((legacyRule: any, index: number) => {
                    const legacyType = legacyRule?.ruleType;
                    const mode: AccessRuleMode = legacyType === "QUERY_BASED" ? AccessRuleMode.QUERY : AccessRuleMode.ROLE;
                    const action: AccessAction = legacyRule?.action === AccessAction.HIDE ? AccessAction.HIDE : AccessAction.SHOW;

                    return {
                        id: uuidv4(),
                        name:
                            typeof legacyRule?.name === "string" && legacyRule.name.trim()
                                ? legacyRule.name
                                : `قانون ${index + 1}`,
                        mode,
                        allRoles: false,
                        roleIds: [],
                        query: typeof legacyRule?.query === "string" ? legacyRule.query : "",
                        columns: Array.isArray(legacyRule?.columns)
                            ? legacyRule.columns
                                .map((c: any) => c?.name)
                                .filter((x: unknown) => typeof x === "string")
                            : [],
                        action,
                        status: legacyRule?.isActive === false
                            ? AccessRuleStatus.INACTIVE
                            : AccessRuleStatus.ACTIVE
                    };
                });

                return {
                    version: 2,
                    tableId: typeof asAny.tableId === "string" ? asAny.tableId : "",
                    rules: legacyRules
                };
            }

            return null;
        } catch {
            console.error("Failed to parse table access config");
            return null;
        }
    }

    /**
     * Creates a new empty access rule
     */
    static createRule(name: string): AccessRule {
        return {
            id: uuidv4(),
            name,
            mode: AccessRuleMode.ROLE,
            allRoles: false,
            roleIds: [],
            userIds: [],
            query: "",
            columns: [],
            action: AccessAction.SHOW,
            status: AccessRuleStatus.ACTIVE
        };
    }

    /**
     * Creates a new access configuration
     */
    static createConfig(tableId: string): TableAccessConfig {
        return {
            version: 2,
            tableId,
            rules: []
        };
    }

    /**
     * Adds a new rule to the configuration
     */
    static addRule(config: TableAccessConfig, rule: AccessRule): TableAccessConfig {
        return {
            ...config,
            rules: [...config.rules, rule]
        };
    }

    /**
     * Updates an existing rule
     */
    static updateRule(
        config: TableAccessConfig,
        ruleId: string,
        updates: Partial<AccessRule>
    ): TableAccessConfig {
        return {
            ...config,
            rules: config.rules.map((rule) =>
                rule.id === ruleId ? { ...rule, ...updates } : rule
            )
        };
    }

    /**
     * Removes a rule from the configuration
     */
    static removeRule(config: TableAccessConfig, ruleId: string): TableAccessConfig {
        return {
            ...config,
            rules: config.rules.filter((rule) => rule.id !== ruleId)
        };
    }

    /**
     * Gets rule by ID
     */
    static getRule(config: TableAccessConfig, ruleId: string): AccessRule | undefined {
        return config.rules.find((rule) => rule.id === ruleId);
    }

    /**
     * Validates rule configuration
     */
    static isValidRule(rule: AccessRule, requireColumns = true): boolean {
        if (!rule.name?.trim()) return false;
        if (requireColumns && (!rule.columns || rule.columns.length === 0))
            return false;

        if (rule.mode === AccessRuleMode.ROLE) {
            if (!rule.allRoles && rule.roleIds.length === 0) return false;
            return true;
        }

        if (rule.mode === AccessRuleMode.USER) {
            return rule.userIds.length > 0;
        }

        return !!rule.query.trim();
    }

    /**
     * Gets active rules (can be used for filtering on backend)
     */
    static getActiveRules(config: TableAccessConfig): AccessRule[] {
        return config.rules.filter((rule) => rule.status === AccessRuleStatus.ACTIVE);
    }

    /**
     * Extracts all columns from rules
     */
    static getAllColumns(config: TableAccessConfig): Set<string> {
        const columns = new Set<string>();
        config.rules.forEach((rule) => {
            rule.columns.forEach((col) => columns.add(col));
        });
        return columns;
    }
}
